import React, { useState } from "react";
import { Heart, Lightbulb, Users, Rocket, Shield, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import ContactFormDialog from "@/components/ContactFormDialog";

const Nonprofits: React.FC = () => {
  const [contactOpen, setContactOpen] = useState(false);

  const canonicalUrl = `https://buildwithchrist.ai/nonprofits`;

  return (
    <div className="relative">
      <Helmet>
        <title>Nonprofits - Become a Mission Partner | BuildWithChrist.ai</title>
        <meta name="description" content="Partner with BuildWithChrist.ai to receive AI-powered tools built specifically for your mission. We help nonprofits accelerate their impact through focused build events." />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <ContactFormDialog 
        open={contactOpen} 
        onOpenChange={setContactOpen} 
        defaultReason="Mission Partnership"
      />

      {/* Hero Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[100px]" />
        <div className="max-w-4xl mx-auto text-center relative z-10 space-y-6">
          <h1 className="text-4xl md:text-6xl font-bold leading-tight font-display">
            <span className="text-gradient">Become a Mission Partner</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Let us build AI-powered tools to support your mission. Our teams create solutions designed specifically for your needs — at no cost to you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button size="lg" className="text-lg" onClick={() => setContactOpen(true)}>
              Apply to Be a Partner
            </Button>
            <Button size="lg" variant="outline" className="text-lg" asChild>
              <a href="#how-it-works">Learn More</a>
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 px-4 bg-card/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 font-display">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <span className="text-2xl font-bold text-primary">1</span>
              </div>
              <h3 className="font-bold text-lg">Share Your Challenge</h3>
              <p className="text-sm text-muted-foreground">Tell us about a problem your organization faces that AI might help solve. We'll work with you to scope a focused project.</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto">
                <span className="text-2xl font-bold text-secondary">2</span>
              </div>
              <h3 className="font-bold text-lg">We Build Together</h3>
              <p className="text-sm text-muted-foreground">During our 4-hour build event, a team of skilled volunteers creates a working AI solution tailored to your needs.</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <span className="text-2xl font-bold text-primary">3</span>
              </div>
              <h3 className="font-bold text-lg">You Receive the Tool</h3>
              <p className="text-sm text-muted-foreground">Take ownership of the AI tool with documentation and training. Ongoing support may be available through sponsors.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Look For */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 font-display">What We Look For</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            We partner with organizations whose missions align with human dignity, service, and community flourishing.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-card border border-border p-6 rounded-xl">
              <Heart className="text-secondary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Mission-Driven</h3>
              <p className="text-sm text-muted-foreground">Organizations focused on serving others and addressing real community needs.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <Target className="text-primary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Clear Challenge</h3>
              <p className="text-sm text-muted-foreground">A specific problem or workflow that could benefit from AI-powered automation or insight.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <Users className="text-secondary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Engaged Team</h3>
              <p className="text-sm text-muted-foreground">At least one team member available to participate and provide input during the build event.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <Shield className="text-primary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Values Alignment</h3>
              <p className="text-sm text-muted-foreground">Commitment to ethical use of AI that respects human dignity and serves the common good.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <Lightbulb className="text-secondary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Open to Innovation</h3>
              <p className="text-sm text-muted-foreground">Willingness to explore new approaches and adopt AI tools in your operations.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <Rocket className="text-primary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Ready to Scale</h3>
              <p className="text-sm text-muted-foreground">Potential to leverage AI solutions to expand your impact and serve more people.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Examples */}
      <section className="py-16 px-4 bg-card/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 font-display">What We Can Build</h2>
          <div className="space-y-6">
            <div className="bg-card border border-border p-6 rounded-xl">
              <h3 className="font-bold text-lg mb-2 text-primary">Volunteer Coordination</h3>
              <p className="text-muted-foreground">AI-powered scheduling, matching, and communication tools to manage your volunteer workforce more effectively.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <h3 className="font-bold text-lg mb-2 text-secondary">Donor Engagement</h3>
              <p className="text-muted-foreground">Automated thank-you messages, personalized outreach, and donor journey optimization.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <h3 className="font-bold text-lg mb-2 text-primary">Content & Communications</h3>
              <p className="text-muted-foreground">AI assistants for newsletter writing, social media content, and storytelling that amplifies your mission.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <h3 className="font-bold text-lg mb-2 text-secondary">Data & Reporting</h3>
              <p className="text-muted-foreground">Automated analysis of program data, impact reporting, and insights to guide decision-making.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold font-display">Ready to Accelerate Your Mission?</h2>
          <p className="text-lg text-muted-foreground">
            Apply to become a mission partner. There's no cost to participate — we believe in service, not transactions.
          </p>
          <Button size="lg" className="text-lg" onClick={() => setContactOpen(true)}>
            Apply to Be a Mission Partner
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Nonprofits;
